import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/UserOrders.css";

/**
 * AssignDeliveryToOrders - admin UI to search orders and assign delivery person
 * - Searches orders using /api/user/admin/showorder?orderId=<orderId> (if orderId empty it should return all or you can adjust)
 * - Fetches delivery persons from /api/user/deliveryperson/all
 * - Assigns via POST /api/user/admin/order/assignDelivery (JSON body { orderId, deliveryId })
 *
 * Behavior:
 * - Use the search box and "Search" to fetch orders
 * - Click "Assign" on a card to prefill the assign form
 * - Or type order id and select delivery person in the assign panel and click Assign
 */

const AssignDeliveryToOrders = () => {
  const [orderId, setOrderId] = useState("");
  const [rawOrders, setRawOrders] = useState([]); // flat list returned by backend
  const [groupedOrders, setGroupedOrders] = useState([]);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loading, setLoading] = useState(false);

  const [assignDelivery, setAssignDelivery] = useState({
    orderId: "",
    deliveryId: "",
  });

  // fetch delivery persons
  useEffect(() => {
    const retrieveAllDeliveryPerson = async () => {
      try {
        const response = await axios.get(
          "https://goldenhides.in/api/user/deliveryperson/all"
        );
        return response.data;
      } catch (err) {
        console.error("Failed to fetch delivery persons", err);
        return null;
      }
    };

    const getAllDeliveryPerson = async () => {
      const data = await retrieveAllDeliveryPerson();
      if (data?.users) setDeliveryPersons(data.users);
    };

    getAllDeliveryPerson();
  }, []);

  // fetch orders (optionally by orderId)
  const retrieveAllOrder = async (qOrderId = "") => {
    try {
      // if backend expects empty orderId to return all, pass it; otherwise you can change to /allorder
      const url =
        "https://goldenhides.in/api/user/admin/showorder" +
        (qOrderId ? `?orderId=${encodeURIComponent(qOrderId)}` : "");
      const resp = await axios.get(url);
      return resp.data;
    } catch (err) {
      console.error("Failed to fetch orders", err);
      toast.error("Failed to fetch orders from server");
      return null;
    }
  };

  // initial: optionally don't auto-load all orders — keep it on search; if you want to load all at mount, uncomment below
  // useEffect(() => { handleSearchOrders(); }, []);

  // helper to group flat order rows by orderId
  useEffect(() => {
    if (!rawOrders || rawOrders.length === 0) {
      setGroupedOrders([]);
      return;
    }
    const map = new Map();
    rawOrders.forEach((o) => {
      const id = o.orderId ?? `order_${Math.random().toString(36).slice(2, 9)}`;
      if (!map.has(id)) {
        map.set(id, {
          orderId: id,
          orderDate: o.orderDate,
          deliveryDate: o.deliveryDate,
          deliveryStatus: o.deliveryStatus,
          deliveryPersonName: o.deliveryPersonName,
          deliveryPersonContact: o.deliveryPersonContact,
          userName: o.userName,
          userPhone: o.userPhone,
          deliveryAddress: o.deliveryAddress ?? {},
          items: [],
        });
      }

      map.get(id).items.push({
        productId: o.productId,
        productName: o.productName,
        productDescription: o.productDescription,
        productImage: o.productImage,
        quantity: o.quantity,
        size: o.size,
        totalPrice: o.totalPrice,
      });
    });

    setGroupedOrders(Array.from(map.values()));
  }, [rawOrders]);

  const handleSearchOrders = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    const data = await retrieveAllOrder(orderId.trim());
    setLoading(false);
    if (data) {
      // backend returns { orders: [...] } or array
      const orders = data.orders ?? data;
      setRawOrders(Array.isArray(orders) ? orders : []);
    } else {
      setRawOrders([]);
    }
  };

  const handleInput = (e) => {
    setAssignDelivery({ ...assignDelivery, [e.target.name]: e.target.value });
  };

  const clearAssignForm = () => {
    setAssignDelivery({ orderId: "", deliveryId: "" });
  };

  const assignDeliveryToOrders = async (e) => {
    if (e) e.preventDefault();
    if (!assignDelivery.orderId) {
      toast.error("Please enter/select Order Id");
      return;
    }
    if (!assignDelivery.deliveryId) {
      toast.error("Please select a delivery person");
      return;
    }

    try {
      const resp = await axios.post(
        "https://goldenhides.in/api/user/admin/order/assignDelivery",
        assignDelivery,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      const res = resp.data;
      if (res.success) {
        toast.success(res.responseMessage || "Assigned successfully", {
          position: "top-center",
          autoClose: 1500,
        });
        // update local orders if server returned updated orders
        if (res.orders) {
          const orders = res.orders;
          setRawOrders(Array.isArray(orders) ? orders : []);
        } else {
          // refresh the currently displayed list (re-run search)
          await handleSearchOrders();
        }
        clearAssignForm();
      } else {
        toast.error(res.responseMessage || "Failed to assign delivery person", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    } catch (err) {
      console.error("Assign error", err);
      toast.error("Server error while assigning delivery person", {
        position: "top-center",
        autoClose: 2000,
      });
    }
  };

  const formatDateFromEpoch = (epochTime) => {
    if (!epochTime) return "";
    const d = new Date(Number(epochTime));
    const opts = { day: "2-digit", month: "short", year: "numeric" };
    const datePart = d.toLocaleDateString(undefined, opts);
    const timePart = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart}, ${timePart}`;
  };

  // Pre-fill assign form for a given orderId (used by "Assign" button on card)
  const prefillAssignForOrder = (id) => {
    setAssignDelivery((prev) => ({ ...prev, orderId: id }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mt-3 px-2">
      <ToastContainer />
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-column flex-md-row">
          <div className="text-start">
            <h2 className="fw-bold text-success mb-0">Assign Delivery</h2>
            <p className="text-muted mb-0">
              Search orders and assign delivery persons
            </p>
          </div>
        </div>

        {/* Top: Search panel */}
        <div className="card form-card ms-2 me-2 mb-4 custom-bg">
          <div className="card-header text-center bg-color custom-bg-text">
            <h4>Search Customer Orders</h4>
          </div>
          <div className="card-body" style={{ overflowY: "auto" }}>
            <form className="row g-3" onSubmit={handleSearchOrders}>
              <div className="col-auto">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Order Id..."
                  onChange={(e) => setOrderId(e.target.value)}
                  value={orderId}
                />
              </div>
              <div className="col-auto">
                <button
                  type="submit"
                  className="btn bg-color custom-bg-text mb-3"
                >
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary ms-2"
                  onClick={() => {
                    setOrderId("");
                    setRawOrders([]);
                    setGroupedOrders([]);
                  }}
                >
                  Clear
                </button>
              </div>
            </form>

            {/* results are shown as grouped cards below (so we keep a table removed) */}
            {loading ? (
              <div className="text-center mt-3">Loading orders...</div>
            ) : groupedOrders.length === 0 && rawOrders.length === 0 ? (
              <div className="text-center mt-3 text-muted">
                No orders to show. Use search to find orders.
              </div>
            ) : null}
          </div>
        </div>

        {/* Assign form */}
        <div className="card form-card ms-2 me-2 mb-4 border-color custom-bg">
          <div className="card-header text-center bg-color custom-bg-text">
            <h4>Assign Delivery To Orders</h4>
          </div>
          <div className="card-body text-color">
            <form className="row g-3" onSubmit={assignDeliveryToOrders}>
              <div className="col-md-4">
                <label>
                  <b>Order Id</b>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Order Id..."
                  name="orderId"
                  onChange={handleInput}
                  value={assignDelivery.orderId}
                />
              </div>

              <div className="col-md-4">
                <label>
                  <b>Delivery Person</b>
                </label>
                <select
                  onChange={handleInput}
                  className="form-control"
                  name="deliveryId"
                  value={assignDelivery.deliveryId}
                >
                  <option value="">Select Delivery Person</option>

                  {deliveryPersons.map((person) => {
                    return (
                      <option key={person.id} value={person.id}>
                        {person.firstName}{" "}
                        {person.lastName ? person.lastName : ""}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="col-md-4 d-flex align-items-end gap-2">
                <button type="submit" className="btn bg-color custom-bg-text">
                  Assign Delivery Person
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={clearAssignForm}
                >
                  Clear
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Grouped order cards (search results) */}
        <div className="mb-4">
          {groupedOrders.map((order) => {
            const orderTotal = order.items.reduce(
              (acc, it) => acc + Number(it.totalPrice || 0),
              0
            );

            const showDeliveryInfo =
              order.deliveryPersonName &&
              order.deliveryPersonContact &&
              order.deliveryPersonName !== "Pending" &&
              order.deliveryPersonContact !== "Pending";

            return (
              <div
                key={order.orderId}
                className="card form-card ms-2 me-2 mb-4 custom-bg orders-group-card"
              >
                {/* Order header */}
                <div className="card-header d-flex align-items-center justify-content-between bg-white">
                  <div className="d-flex align-items-center gap-4">
                    <div>
                      <div className="small text-muted">Order ID</div>
                      <div style={{ fontWeight: 700 }}>#{order.orderId}</div>
                    </div>

                    <div>
                      <div className="small text-muted">Items</div>
                      <div style={{ fontWeight: 600 }}>
                        {order.items.length} item
                        {order.items.length > 1 ? "s" : ""}
                      </div>
                    </div>

                    <div>
                      <div className="small text-muted">Ordered</div>
                      <div style={{ fontWeight: 600 }}>
                        {formatDateFromEpoch(order.orderDate)}
                      </div>
                    </div>

                    <div>
                      <div className="small text-muted">Customer</div>
                      <div style={{ fontWeight: 600 }}>
                        {order.userName || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div className="text-end small">
                      <div className="small text-muted">Contact</div>
                      <div style={{ fontWeight: 600 }}>
                        {order.userPhone || "-"}
                      </div>
                    </div>

                    {showDeliveryInfo && (
                      <div className="text-end small">
                        <div className="small text-muted">Delivery Person</div>
                        <div style={{ fontWeight: 600 }}>
                          {order.deliveryPersonName}
                        </div>
                        <div className="small">
                          {order.deliveryPersonContact}
                        </div>
                      </div>
                    )}

                    <div>
                      <span
                        className={`badge order-status-badge ${
                          (order.deliveryStatus || "").toLowerCase() ===
                          "pending"
                            ? "badge-pending"
                            : "badge-other"
                        }`}
                      >
                        {order.deliveryStatus || "Pending"}
                      </span>
                    </div>

                    <div>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => prefillAssignForOrder(order.orderId)}
                      >
                        Assign
                      </button>
                    </div>
                  </div>
                </div>

                {/* Body: product cards */}
                <div className="card-body">
                  <div className="row g-3">
                    {order.items.map((item, idx) => (
                      <div
                        key={`${order.orderId}_item_${idx}`}
                        className="col-12 col-md-4"
                      >
                        <div className="order-product-card p-3 h-100 shadow-sm rounded">
                          <div className="d-flex gap-3">
                            <div style={{ width: 90, height: 90 }}>
                              {item.productImage ? (
                                <img
                                  src={`https://goldenhides.in/api/product/${item.productImage}`}
                                  alt={item.productName}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    background: "#f1f1f1",
                                    borderRadius: 6,
                                  }}
                                />
                              )}
                            </div>

                            <div className="flex-grow-1">
                              <div style={{ fontWeight: 700 }}>
                                {item.productName}
                              </div>
                              <div className="small text-muted mt-1">
                                {item.productDescription
                                  ? item.productDescription.slice(0, 80) +
                                    (item.productDescription.length > 80
                                      ? "..."
                                      : "")
                                  : ""}
                              </div>

                              <div className="mt-2 small text-muted">
                                Qty: {item.quantity} &nbsp;|&nbsp; Size:{" "}
                                {item.size || "-"}
                              </div>
                            </div>

                            <div style={{ textAlign: "right", minWidth: 90 }}>
                              <div
                                className="text-success"
                                style={{ fontWeight: 700 }}
                              >
                                ₹ {Number(item.totalPrice || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer: order total & address */}
                <div className="card-footer bg-white d-flex justify-content-between align-items-center">
                  <div className="small text-muted">
                    <div>Delivery Address:</div>
                    <div style={{ fontWeight: 600 }}>
                      {order.deliveryAddress?.addressLine ?? "-"},{" "}
                      {order.deliveryAddress?.city ?? "-"}{" "}
                      {order.deliveryAddress?.downtown
                        ? `, ${order.deliveryAddress.downtown}`
                        : ""}
                    </div>
                  </div>

                  <div
                    style={{ fontWeight: 700, color: "#0a7a3b", fontSize: 18 }}
                  >
                    Total ₹ {orderTotal.toFixed(2)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AssignDeliveryToOrders;
